import { ReactComponent as NewIcon } from 'assets/svg/badgets/new_client.svg';
import { ReactComponent as GoodIcon } from 'assets/svg/icons/defencer.svg';
import { ReactComponent as BadIcon } from 'assets/svg/icons/warning_user.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as PhoneIcon } from '../../../../assets/svg/icons/chiamate.svg';
import { ReactComponent as MailIcon } from '../../../../assets/svg/icons/mail.svg';
import { ReactComponent as WhatsappIcon } from '../../../../assets/svg/icons/whatsapp.svg';
import { ReactComponent as Verified } from '../../../../assets/svg/verified.svg';
import AvatarIcon from '../../../Common/AvatarIcon';
import styles from './ClientModal.module.scss';

import clx from 'classnames';
import { allBadClients } from 'Containers/Clients/store/selectors';
import ConfirmModal from 'Containers/Common/ConfirmModal';
import IOSSwitch from 'Containers/Common/CustomSwitch';
import dayjs from 'dayjs';
import {
  BarChart3,
  CalendarClock,
  ChevronDown,
  History,
  KeyRound,
  Pencil,
  Plus,
  StickyNote,
  Trash2,
  UserCheck,
  UserX,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveSalonePaymentInfo } from 'utils/salonToken';
import { sendMessageToApp } from 'utils/exsternalCall';
import useWindowSize from '../../../../utils/useWindowSize';
import { getActiveSalonId } from '../../../Auth/store/selectors';
import ConfirmDeleteModal from '../../../Common/ConfirmDeleteModal';
import ConfirmSecondaryModal from '../../../Common/ConfirmSecondaryModal';
import ButtonSelector from '../../../Common/CustomSelector/ButtonSelector';
import HoveredInfoIcon from '../../../Common/HoveredInfoIcon';
import { salonManagementActions } from '../../../SalonManagement/store';
import { getWorkers } from '../../../SalonManagement/store/selectors';
import { clientActions } from '../../store';
import { getClient, selectClientsShort } from '../../store/selectors';
import { getClientStatuses } from '../constants';
import DeleteNote from './DeleteNote';
import EditNote from './EditNote';
import EditPhone from './EditPhone';
import EditPassword from './EditPassword';
import { useTranslation } from 'react-i18next';

const formatDate = (value, fmt = 'DD MMMM YYYY') => {
  const date = dayjs(value);
  return value && date.isValid() ? date.format(fmt) : '—';
};

const phoneDigits = (value) => (value || '').replaceAll(/\D/g, '');

/** Storico appuntamenti: prime N righe, poi "Carica altre". */
const HISTORY_PAGE_SIZE = 4;

export default ({ client_id, totPren, closeModal }) => {
  const { t } = useTranslation();
  const clientStatusOptions = useMemo(() => getClientStatuses(), [t]);
  const badClients = useSelector(allBadClients());
  const dispatch = useDispatch();
  const client = useSelector(getClient());
  const clientsList = useSelector(selectClientsShort);
  const workers = useSelector(getWorkers(0, false));
  const { isMobile } = useWindowSize();
  const [deleteClientModalIsOpen, setDeleteClientModalIsOpen] = useState(false);
  const [enableClientModalIsOpen, setEnableClientModalIsOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditPasswordModalOpen, setIsEditPasswordModalOpen] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState(false);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);
  const [isModalNoteData, setIsModalNoteData] = useState({});
  const isBadClient = badClients?.find((a) => a?.utente === client_id);

  const [deleteClientAppointment, setDeleteClientAppointment] = useState(false);
  const activeSalonId = useSelector(getActiveSalonId());
  const listClient = useMemo(
    () => clientsList.find((item) => +item.value === +client_id),
    [clientsList, client_id],
  );
  const totalBookingsCount = totPren ?? listClient?.totAppointments ?? null;
  const workersById = useMemo(
    () =>
      (workers || []).reduce((acc, worker) => {
        const name = worker.nome_referente || worker.name;
        if (worker.idpoltrona != null && worker.idpoltrona !== '') {
          acc[worker.idpoltrona] = name;
          acc[String(worker.idpoltrona)] = name;
        }
        if (worker.id != null && worker.id !== '') {
          acc[worker.id] = name;
          acc[String(worker.id)] = name;
        }
        return acc;
      }, {}),
    [workers],
  );
  const getWorkerName = useCallback(
    (appointment) => {
      const workerId = appointment?.worker ?? appointment?.worker_id;
      if (workerId == null || workerId === '') {
        return t('clients.clientModal.main.unavailableChair');
      }
      return (
        workersById[workerId] ??
        workersById[String(workerId)] ??
        t('clients.clientModal.main.unavailableChair')
      );
    },
    [workersById, t],
  );
  useEffect(() => {
    if (client?.idutente != client_id) {
      dispatch(clientActions.GET_CLIENT.REQUEST({ idcliente: client_id }));
    }
  }, [dispatch, client_id]);
  useEffect(() => {
    activeSalonId && dispatch(salonManagementActions.GET_WORKERS.REQUEST({}, null, { skipLoader: true }));
    return () => dispatch(salonManagementActions.GET_WORKERS.CLEAR());
  }, [activeSalonId]);
  const activeAppointments = useMemo(
    () =>
      (client.appointments || []).filter(
        (appointment) => !dayjs().isAfter(appointment.startDate, 'day'),
      ),
    [client.appointments],
  );
  const oldAppointments = useMemo(
    () =>
      (client.appointments || []).filter((appointment) =>
        dayjs().isAfter(appointment.startDate, 'day'),
      ),
    [client.appointments],
  );
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE_SIZE);
  useEffect(() => {
    setHistoryLimit(HISTORY_PAGE_SIZE);
  }, [client_id]);
  const visibleOldAppointments = oldAppointments.slice(0, historyLimit);
  const hiddenHistoryCount = Math.max(0, oldAppointments.length - historyLimit);
  const clientNotes = client.notes;
  const paymentDisabled = getActiveSalonePaymentInfo();
  const fromApp = localStorage.getItem('fromApp') == 'true';
  const isDisabled = client.disabilitato == 1;
  const dash = t('clients.clientModal.main.emptyValue');

  const handleCheckChange = (event) => {
    event.preventDefault();
    dispatch(
      clientActions.EDIT_CLIENT.REQUEST({
        idutente: client_id,
        tipo: event.target.checked ? 6 : 0,
      }),
    );
  };

  const openAddNote = useCallback(() => {
    setIsAddingNote(true);
    setNewNoteText('');
  }, []);

  const cancelAddNote = useCallback(() => {
    setIsAddingNote(false);
    setNewNoteText('');
    setIsSavingNote(false);
  }, []);

  const saveNewNote = useCallback(() => {
    const nota = newNoteText.trim();
    if (!nota || isSavingNote) return;

    setIsSavingNote(true);
    dispatch(
      clientActions.ADD_NOTE.REQUEST(
        {
          idcliente: client_id,
          nota,
        },
        () => {
          dispatch(
            clientActions.GET_CLIENT.REQUEST({ idcliente: client_id }, () => {
              setIsSavingNote(false);
              cancelAddNote();
            }),
          );
        },
      ),
    );
    window.setTimeout(() => setIsSavingNote(false), 2500);
  }, [cancelAddNote, client_id, dispatch, isSavingNote, newNoteText]);

  const handleContact = (type) => {
    if (type === 'phone' && client.phone) {
      const n = phoneDigits(client.phone);
      if (!fromApp) window.location.href = `tel:${n}`;
      else sendMessageToApp('app:phone', { number: `+${n.length > 9 ? n : '39' + n}` });
      return;
    }
    if (type === 'email' && client.email) {
      if (!fromApp) window.location.href = `mailto:${client.email}`;
      else sendMessageToApp('app:mail', { email: `${client.email}` });
      return;
    }
    if (type === 'whatsapp' && client.phone) {
      const n = phoneDigits(client.phone);
      if (!fromApp) window.open(`https://wa.me/${n}`, '_blank');
      else sendMessageToApp('app:whatsapp', { number: `+${n.length > 9 ? n : '39' + n}`, text: 'Ciao' });
    }
  };

  const favoriteTimes =
    client.favoriteTimes?.map((time) => time.nomeFascia).filter(Boolean).join(' · ') || dash;

  return (
    <>
      <ConfirmSecondaryModal
        isModalOpen={deleteClientModalIsOpen}
        setIsModalOpen={setDeleteClientModalIsOpen}
        title={t('clients.clientModal.confirmDisable.title')}
        subtitle={t('clients.clientModal.confirmDisable.subtitle')}
        buttons={[t('common.actions.disable')]}
        buttonsActions={[
          () =>
            dispatch(
              clientActions.DELETE_CLIENT.REQUEST(
                {
                  idUtente: client_id,
                },
                closeModal,
              ),
            ),
        ]}
      />

      <EditPhone
        isModalOpen={isEditModalOpen}
        setIsModalOpen={setIsEditModalOpen}
        phone={client?.phone}
        verified={client?.phoneVerified}
        client_id={client_id}
      />

      <EditPassword
        isModalOpen={isEditPasswordModalOpen}
        setIsModalOpen={setIsEditPasswordModalOpen}
        client_id={client_id}
      />

      <EditNote
        isModalOpen={isEditNoteModalOpen}
        setIsModalOpen={setIsEditNoteModalOpen}
        client_id={client_id}
        nota={isModalNoteData}
      />

      <DeleteNote
        isModalOpen={isDeleteNoteModalOpen}
        setIsModalOpen={setIsDeleteNoteModalOpen}
        client_id={client_id}
        nota={isModalNoteData}
      />

      <ConfirmModal
        isModalOpen={enableClientModalIsOpen}
        setIsModalOpen={setEnableClientModalIsOpen}
        title={t('clients.clientModal.confirmEnable.title')}
        action={() =>
          dispatch(
            clientActions.ENABLE_CLIENT.REQUEST(
              {
                idUtente: client_id,
              },
              closeModal,
            ),
          )
        }
      />
      <ConfirmDeleteModal
        isModalOpen={deleteClientAppointment}
        setIsModalOpen={setDeleteClientAppointment}
        title={t('clients.clientModal.confirmDisableAppointment.title')}
        buttons={[t('common.actions.cancel'), t('common.actions.disable')]}
        action={() =>
          dispatch(
            clientActions.DELETE_CLIENT_APPOINTMENT.REQUEST(deleteClientAppointment, () =>
              setDeleteClientAppointment(false),
            ),
          )
        }
      />
      <div
        className={clx(styles.client_modal_container, {
          [styles.client_modal_container_mobile]: isMobile,
        })}
      >
        <div className={styles.client_modal_body}>
          <div className={styles.profile_header}>
            <div className={styles.avatar_block}>
              <div className={styles.avatar_container}>
                <AvatarIcon name={client.name} icon={client.icon} size={'64px'} />
                {totalBookingsCount != null && totalBookingsCount < 2 && (
                  <NewIcon className={styles.new_badge} />
                )}
              </div>
              <div className={styles.profile_copy}>
                <div className={styles.client_name}>{client.name || dash}</div>
                <div className={styles.profile_meta}>
                  {totalBookingsCount != null && (
                    <span>
                      {t('clients.clientModal.main.bookingsCount', { count: totalBookingsCount })}
                    </span>
                  )}
                  {client.lastAccess && (
                    <span>
                      {t('clients.clientModal.main.lastAccess')} {formatDate(client.lastAccess)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.status_selector_container}>
              <div className={styles.section_label}>
                {t('clients.clientModal.main.statusSelectorTitle')}
              </div>
              <ButtonSelector
                value={client.tipo}
                setValue={(value) => {
                  dispatch(
                    clientActions.EDIT_CLIENT.REQUEST({
                      idutente: client_id,
                      tipo: value,
                    }),
                  );
                }}
                options={clientStatusOptions}
                marginLeft={0}
                width={'100%'}
                height={'44px'}
              />
            </div>
          </div>

          {isBadClient && client.tipo != 6 && (
            <div className={styles.bad_section}>
              <BadIcon />
              <div className={styles.alert_copy}>
                <div className={styles.alert_title}>{t('clients.clientModal.main.bad.title')}</div>
                <div className={styles.alert_description}>
                  {t('clients.clientModal.main.bad.description')}
                </div>
              </div>
            </div>
          )}

          {client.tipo == 6 && (
            <div className={styles.good_section}>
              <GoodIcon />
              <div className={styles.alert_copy}>
                <div className={styles.alert_title}>{t('clients.clientModal.main.good.title')}</div>
                <div className={styles.alert_description}>
                  {t('clients.clientModal.main.good.description')}
                </div>
              </div>
            </div>
          )}

          <div className={styles.info_card}>
            {paymentDisabled !== 1 && (
              <div className={styles.info_row}>
                <span className={styles.info_label}>{t('clients.clientModal.main.payOnlyInApp')}</span>
                <div className={styles.info_value}>
                  <IOSSwitch checked={client.tipo === 6} onChange={handleCheckChange} />
                </div>
              </div>
            )}
            <div className={styles.info_row}>
              <span className={styles.info_label}>
                {t('clients.clientModal.main.registrationDate')}
              </span>
              <div className={styles.info_value}>{formatDate(client.registrationDate)}</div>
            </div>
            <div className={styles.info_row}>
              <span className={styles.info_label}>{t('clients.clientModal.main.lastAccess')}</span>
              <div className={styles.info_value}>{formatDate(client.lastAccess)}</div>
            </div>
            <div className={styles.info_row}>
              <span className={styles.info_label}>{t('clients.clientModal.main.phone')}</span>
              <div className={styles.info_value}>
                <span className={styles.phone}>{client.phone || dash}</span>
                {client.phoneVerified ? (
                  <span
                    className={styles.verified_badge}
                    title={t('clients.clientModal.main.verifiedPhone')}
                  >
                    <Verified />
                  </span>
                ) : null}
                <button
                  type="button"
                  className={styles.icon_action}
                  onClick={() => setIsEditModalOpen(true)}
                  aria-label={
                    client.phone
                      ? t('clients.clientModal.main.editPhone')
                      : t('clients.clientModal.main.addPhone')
                  }
                >
                  <Pencil size={14} strokeWidth={2.2} />
                </button>
              </div>
            </div>
            <div className={styles.info_row}>
              <span className={styles.info_label}>{t('clients.clientModal.main.email')}</span>
              <div className={styles.info_value}>
                <span>{client.email || dash}</span>
              </div>
            </div>
            <div className={styles.info_row}>
              <span className={styles.info_label}>{t('clients.clientModal.main.password')}</span>
              <div className={styles.info_value}>
                <button
                  type="button"
                  className={styles.change_password_button}
                  onClick={() => setIsEditPasswordModalOpen(true)}
                >
                  <KeyRound size={14} strokeWidth={2.2} aria-hidden="true" />
                  {t('clients.clientModal.main.changePassword')}
                </button>
              </div>
            </div>
          </div>

          <div className={styles.section_label}>
            {t('clients.clientModal.main.contactClient')}
          </div>
          <div className={styles.contact_actions}>
            <button
              type="button"
              className={styles.contact_chip}
              disabled={!client.phone}
              onClick={() => handleContact('whatsapp')}
            >
              <WhatsappIcon />
              <span>{t('clients.clientModal.main.contactWhatsapp')}</span>
            </button>
            <button
              type="button"
              className={styles.contact_chip}
              disabled={!client.phone}
              onClick={() => handleContact('phone')}
            >
              <PhoneIcon />
              <span>{t('clients.clientModal.main.contactCall')}</span>
            </button>
            <button
              type="button"
              className={styles.contact_chip}
              disabled={!client.email}
              onClick={() => handleContact('email')}
            >
              <MailIcon />
              <span>{t('clients.clientModal.main.contactEmail')}</span>
            </button>
          </div>

          <div className={styles.section_header}>
            <div className={styles.section_label}>
              <StickyNote size={14} strokeWidth={2.4} aria-hidden="true" />
              {t('clients.clientModal.main.notesHistory')}
            </div>
            <button
              type="button"
              className={styles.aggiungi_nota}
              onClick={isAddingNote ? cancelAddNote : openAddNote}
            >
              {isAddingNote
                ? t('common.actions.cancel')
                : t('clients.clientModal.main.addNote')}
              {!isAddingNote ? <Plus size={16} strokeWidth={2.4} aria-hidden="true" /> : null}
            </button>
          </div>
          <div className={styles.notes_block}>
            {isAddingNote ? (
              <div className={styles.note_compose}>
                <label className={styles.note_compose_label} htmlFor="client-new-note">
                  {t('clients.clientModal.addNote.descriptionLabel')}
                </label>
                <textarea
                  id="client-new-note"
                  className={styles.note_compose_input}
                  rows={4}
                  placeholder={t('clients.clientModal.addNote.textareaPlaceholder')}
                  value={newNoteText}
                  onChange={(event) => setNewNoteText(event.target.value)}
                  disabled={isSavingNote}
                  autoFocus
                />
                <div className={styles.note_compose_actions}>
                  <button
                    type="button"
                    className={styles.note_compose_cancel}
                    onClick={cancelAddNote}
                    disabled={isSavingNote}
                  >
                    {t('common.actions.cancel')}
                  </button>
                  <button
                    type="button"
                    className={styles.note_compose_save}
                    onClick={saveNewNote}
                    disabled={isSavingNote || !newNoteText.trim()}
                  >
                    {t('common.actions.save')}
                  </button>
                </div>
              </div>
            ) : null}
            {clientNotes?.length ? (
              clientNotes.map((note) => (
                <div key={note.idutentenota} className={styles.note_row}>
                  <div className={styles.note_copy}>
                    <div className={styles.note_text}>{note?.nota}</div>
                    <div className={styles.note_date}>
                      {dayjs(note.created_at).format('DD/MM/YY HH:mm')}
                    </div>
                  </div>
                  <div className={styles.note_actions}>
                    <button
                      type="button"
                      className={styles.edit_note}
                      onClick={() => {
                        setIsModalNoteData(note);
                        setIsEditNoteModalOpen(true);
                      }}
                      aria-label={t('clients.clientModal.editNote.title')}
                    >
                      <Pencil size={16} strokeWidth={2.2} />
                    </button>
                    <button
                      type="button"
                      className={styles.delete_note}
                      onClick={() => {
                        setIsModalNoteData(note);
                        setIsDeleteNoteModalOpen(true);
                      }}
                      aria-label={t('clients.clientModal.deleteNote.title')}
                    >
                      <Trash2 size={16} strokeWidth={2.2} />
                    </button>
                  </div>
                </div>
              ))
            ) : !isAddingNote ? (
              <button
                type="button"
                className={styles.empty_state}
                onClick={openAddNote}
              >
                {t('clients.clientModal.main.noNotes')}
              </button>
            ) : null}
          </div>

          <div className={styles.section_label}>
            <CalendarClock size={14} strokeWidth={2.4} aria-hidden="true" />
            {t('clients.clientModal.main.appointments')}
          </div>
          <div className={styles.active_appointments_block}>
            {activeAppointments.length ? (
              activeAppointments.map((appointment) => (
                <div key={appointment.id} className={styles.appointment_container}>
                  <div className={styles.appointment_info}>
                    <div className={styles.appointment_info_value}>
                      {appointment?.services?.length
                        ? appointment.services.join(' + ')
                        : t('clients.clientModal.main.serviceNone')}
                    </div>
                    <div className={styles.appointment_info_meta}>
                      {dayjs(appointment.startDate).format('dddd DD MMMM')}{' '}
                      {t('clients.clientModal.main.dateAt')}{' '}
                      {dayjs(appointment.startTime, 'HH:mm:ss').format('HH:mm')}
                    </div>
                    <div className={styles.appointment_info_meta}>
                      {t('clients.clientModal.main.with', {
                        name: getWorkerName(appointment),
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setDeleteClientAppointment({
                        idPrenotazione: appointment.id,
                        idPoltrona: appointment.worker_id,
                        deleteAllRicorrenti: false,
                      })
                    }
                    className={styles.appointment_action}
                  >
                    {t('clients.clientModal.main.cancelBooking')}
                  </button>
                </div>
              ))
            ) : (
              <div className={styles.empty_state}>
                {t('clients.clientModal.main.noActiveAppointments')}
              </div>
            )}
          </div>

          <div className={styles.section_label}>
            <BarChart3 size={14} strokeWidth={2.4} aria-hidden="true" />
            {t('clients.clientModal.main.statistics')}
          </div>
          <div className={styles.statistica_block}>
            <div className={styles.statistica_info}>
              <div className={styles.statistica_title}>
                {t('clients.clientModal.main.favoriteDay')}
              </div>
              <div className={styles.statistica_value}>{client.favoriteDay || dash}</div>
            </div>
            <div className={styles.statistica_info}>
              <div className={styles.statistica_title}>
                {t('clients.clientModal.main.averageBookings')}
              </div>
              <div className={styles.statistica_value}>{client.appointmentPerMonth ?? dash}</div>
            </div>
            <div className={styles.statistica_info}>
              <div className={styles.statistica_title}>
                {t('clients.clientModal.main.favoriteService')}
              </div>
              <div className={styles.statistica_value}>{client.favoriteService || dash}</div>
            </div>
            <div className={styles.statistica_info}>
              <div className={styles.statistica_title}>
                {t('clients.clientModal.main.favoriteTime')}
              </div>
              <div className={styles.statistica_value}>{favoriteTimes}</div>
            </div>
            <div className={styles.statistica_info}>
              <div className={styles.statistica_title}>
                {t('clients.clientModal.main.totalBookings')}
              </div>
              <div className={styles.statistica_value}>
                {totalBookingsCount != null ? totalBookingsCount : dash}
              </div>
            </div>
          </div>

          <div className={styles.section_label}>
            <History size={14} strokeWidth={2.4} aria-hidden="true" />
            {t('clients.clientModal.main.appointmentsHistory')}
          </div>
          <div className={styles.appointments_history_block}>
            {oldAppointments.length ? (
              <>
                {visibleOldAppointments.map((appointment) => (
                  <div key={appointment.id} className={styles.appointments_history_row}>
                    <div className={styles.history_copy}>
                      <div className={styles.history_service}>
                        {appointment?.services?.length
                          ? appointment.services.join(' + ')
                          : t('clients.clientModal.main.serviceNone')}
                      </div>
                      <div className={styles.history_meta}>
                        {t('clients.clientModal.main.with', {
                          name: getWorkerName(appointment),
                        })}
                      </div>
                    </div>
                    <div className={styles.appointments_history_date_cell}>
                      {dayjs(appointment.startDate).format('DD/MM/YY')}
                    </div>
                  </div>
                ))}
                {hiddenHistoryCount > 0 ? (
                  <button
                    type="button"
                    className={styles.history_load_more}
                    onClick={() => setHistoryLimit((limit) => limit + HISTORY_PAGE_SIZE)}
                  >
                    <ChevronDown size={16} strokeWidth={2.4} aria-hidden="true" />
                    {t('clients.clientModal.main.loadMoreHistory', {
                      count: Math.min(hiddenHistoryCount, HISTORY_PAGE_SIZE),
                    })}
                  </button>
                ) : null}
              </>
            ) : (
              <div className={styles.empty_state}>
                {t('clients.clientModal.main.noOldAppointments')}
              </div>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.footer_copy}>
            <div className={styles.title_wrapper}>
              <div className={styles.title}>
                {isDisabled
                  ? t('clients.clientModal.main.footer.enableTitle')
                  : t('clients.clientModal.main.footer.disableTitle')}
              </div>
              {!isDisabled && (
                <HoveredInfoIcon
                  description={t('clients.clientModal.main.footer.infoDescription')}
                  sidePosition={'up'}
                />
              )}
            </div>
            <p className={styles.footer_hint}>
              {isDisabled
                ? t('clients.clientModal.main.footer.enableHint')
                : t('clients.clientModal.main.footer.hint')}
            </p>
          </div>

          {!isDisabled && (
            <button
              type="button"
              onClick={() => setDeleteClientModalIsOpen(true)}
              className={styles.delete_button}
            >
              <UserX size={16} strokeWidth={2.2} aria-hidden="true" />
              {t('common.actions.disable')}
            </button>
          )}
          {isDisabled && (
            <button
              type="button"
              onClick={() => setEnableClientModalIsOpen(true)}
              className={styles.enable_button}
            >
              <UserCheck size={16} strokeWidth={2.2} aria-hidden="true" />
              {t('common.actions.enable')}
            </button>
          )}
        </div>
      </div>
    </>
  );
};
